import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FileQuestion, FolderOpen, FolderPlus, HardDrive, Upload } from "lucide-react";
import { useAncestors, useItem, useItems } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Menu, type MenuAnchor, anchorFrom } from "@/components/ui/menu";
import { useDriveActions } from "./driveActions";
import { useUploads } from "./upload/UploadProvider";
import { Breadcrumbs } from "./Breadcrumbs";
import { DriveView } from "./DriveView";
import { EmptyState } from "./EmptyState";
import { buildFolderMenu } from "./items/menuItems";
import { SuggestedFiles } from "./SuggestedFiles";

/** My Drive root and /folder/:folderId. */
export function DrivePage() {
    const { folderId } = useParams();
    const parentId = folderId ?? null;
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const items = useItems(parentId);
    const current = useItem(parentId);
    const ancestors = useAncestors(parentId);
    const actions = useDriveActions();
    const uploads = useUploads();
    const [folderMenu, setFolderMenu] = useState<MenuAnchor | null>(null);
    const [detailsNonce, setDetailsNonce] = useState(0);

    const selectId = params.get("select");
    const clearSelectParam = () => {
        const next = new URLSearchParams(params);
        next.delete("select");
        next.delete("open");
        setParams(next, { replace: true });
    };

    const byIds = (ids: string[]) => {
        const set = new Set(ids);
        return (items.data ?? []).filter((i) => set.has(i.id));
    };

    if (folderId && current.isError) {
        return (
            <div className="flex-1 overflow-auto">
                <EmptyState
                    icon={FileQuestion}
                    tone="slate"
                    title="Folder not found"
                    description="It may have been moved to the bin, deleted, or you may not have access."
                >
                    <Button onClick={() => navigate("/", { replace: true })}>
                        <HardDrive className="h-4 w-4" /> Go to My Drive
                    </Button>
                </EmptyState>
            </div>
        );
    }

    const folder = current.data ?? null;

    return (
        <>
            <DriveView
                variant="folder"
                label={folder ? `Contents of ${folder.name}` : "My Drive"}
                title={
                    <Breadcrumbs
                        trail={ancestors.data ?? []}
                        folderId={parentId}
                        currentName={folder?.name ?? null}
                        onMoveDrop={(ids, target) => void actions.moveTo(byIds(ids), target)}
                        onFilesDrop={(dt, target) => uploads.uploadDataTransfer(dt, target)}
                        onCurrentMenu={(el) => setFolderMenu(anchorFrom(el))}
                    />
                }
                query={items}
                resetKey={`folder:${parentId ?? "root"}`}
                parentId={parentId}
                currentFolder={folder}
                selectId={selectId}
                openSelected={params.get("open") === "1"}
                onSelectHandled={clearSelectParam}
                detailsNonce={detailsNonce}
                aboveList={parentId ? undefined : <SuggestedFiles />}
                emptyState={
                    folder?.ownerEmail ? (
                        // Someone else's folder shared with the user — nothing can be added here.
                        <EmptyState icon={FolderOpen} title="This folder is empty" description={`Shared by ${folder.ownerEmail}.`} />
                    ) : (
                        <EmptyState
                            icon={parentId ? FolderOpen : HardDrive}
                            title={parentId ? "This folder is empty" : "Welcome to your Drive"}
                            description="Drop files or folders here, or use the New button."
                        >
                            <Button onClick={() => void actions.newFolder(parentId)}>
                                <FolderPlus className="h-4 w-4" /> New folder
                            </Button>
                            <Button variant="outline" onClick={() => uploads.pickFiles(parentId)}>
                                <Upload className="h-4 w-4" /> Upload files
                            </Button>
                        </EmptyState>
                    )
                }
            />
            {folderMenu ? (
                <Menu
                    label="Folder actions"
                    anchor={folderMenu}
                    onClose={() => setFolderMenu(null)}
                    entries={buildFolderMenu(folder, parentId, actions, uploads, {
                        onDetails: () => setDetailsNonce((n) => n + 1),
                        onTrashed: () => navigate(folder?.parentId ? `/folder/${encodeURIComponent(folder.parentId)}` : "/")
                    })}
                />
            ) : null}
        </>
    );
}
